import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AiOutlineInstagram } from 'react-icons/ai';
import { FaTiktok, FaAmazon, FaPinterest } from 'react-icons/fa';
import { useCookieConsent } from '../context/CookieConsentContext';
import { useLanguage } from '../context/LanguageContext';
import apiClient from '../api/client';
import './Footer.css';

function FooterLink({ link }) {
  const { type, pageSlug, url, label } = link;
  const href = type === 'page' && pageSlug ? `/page/${pageSlug}` : (url || '#');
  const isExternal = href.startsWith('http');
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer">
        {label || href}
      </a>
    );
  }
  return <Link to={href}>{label || href}</Link>;
}

// Puse nuo viršutinio meniu dydžio
const iconSize = 10;

function Footer() {
  const { openSettings } = useCookieConsent();
  const { language, t } = useLanguage();

  const { data: storeSettings } = useQuery({
    queryKey: ['store-settings'],
    queryFn: async () => {
      const res = await apiClient.get('/store-settings');
      const s = res.data;
      if (s?.logo_url && !s.logo_url.startsWith('http') && !s.logo_url.startsWith('/')) {
        s.logo_url = '/' + s.logo_url;
      }
      if (s?.favicon_url && !s.favicon_url.startsWith('http') && !s.favicon_url.startsWith('/')) {
        s.favicon_url = '/' + s.favicon_url;
      }
      if (s?.footer_logo_url && !s.footer_logo_url.startsWith('http') && !s.footer_logo_url.startsWith('/')) {
        s.footer_logo_url = '/' + s.footer_logo_url;
      }
      return s;
    },
  });

  const { data: footerLinksData } = useQuery({
    queryKey: ['footer-links-public', language],
    queryFn: async () => {
      const res = await apiClient.get('/footer-links/public', { params: { lang: language || 'lt' } });
      return res.data;
    },
    staleTime: 60 * 1000,
  });

  const footerGroups = footerLinksData?.content?.groups ?? [];
  const legalLinks = footerLinksData?.content?.legalLinks ?? [];
  const useDynamicFooter = footerGroups.length > 0;
  const useDynamicLegal = legalLinks.length > 0;

  const rawCompanyName = (storeSettings?.company_name || '').trim();
  const companyName = storeSettings?.company_name?.replace(/\s*parduotuvė\s*/gi, '').trim() || 'Magtic';
  const footerBrandName = rawCompanyName === 'Cloudrix MB' ? 'MAGTIC®' : (companyName || 'MAGTIC®');

  const socialIcons = [
    { key: 'instagram', url: storeSettings?.instagram_url, label: 'Instagram', Icon: AiOutlineInstagram },
    { key: 'tiktok', url: storeSettings?.tiktok_url, label: 'TikTok', Icon: FaTiktok },
    { key: 'amazon', url: storeSettings?.amazon_url, label: 'Amazon', Icon: FaAmazon },
    { key: 'pinterest', url: storeSettings?.pinterest_url, label: 'Pinterest', Icon: FaPinterest },
  ].filter(s => s.url);

  return (
    <footer className="footer">
      <div className="footer-inner">
        <div className="footer-top">
          <div className="footer-brand">
            <div className="footer-contact-links">
              <Link to="/contact?subject=Susisiekti+su+pardavimais" className="footer-contact-card">
                <span className="footer-contact-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7h-8M17 7v8" />
                  </svg>
                </span>
                <span className="footer-contact-label">{t('contactPrefix')}</span>
                <span className="footer-contact-value">{t('salesShort')}</span>
              </Link>
              <Link to="/contact?subject=Susisiekti+su+aptarnavimu" className="footer-contact-card">
                <span className="footer-contact-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7h-8M17 7v8" />
                  </svg>
                </span>
                <span className="footer-contact-label">{t('contactPrefix')}</span>
                <span className="footer-contact-value">{t('serviceShort')}</span>
              </Link>
              <Link to="/contact?subject=Susisiekti+d%C4%97l+kokyb%C4%97s" className="footer-contact-card">
                <span className="footer-contact-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7h-8M17 7v8" />
                  </svg>
                </span>
                <span className="footer-contact-label">{t('contactPrefixAbout')}</span>
                <span className="footer-contact-value">{t('qualityShort')}</span>
              </Link>
              <Link to="/contact?subject=Susisiekti+d%C4%97l+garantijos" className="footer-contact-card">
                <span className="footer-contact-icon" aria-hidden>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7h-8M17 7v8" />
                  </svg>
                </span>
                <span className="footer-contact-label">{t('contactPrefixAbout')}</span>
                <span className="footer-contact-value">{t('warrantyShort')}</span>
              </Link>
            </div>
            {socialIcons.length > 0 ? (
              <div className="footer-social">
                <span className="footer-social-label">{t('followUs')}:</span>
                {socialIcons.map((s) => {
                  const Icon = s.Icon;
                  return (
                    <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer" className="footer-social-link" aria-label={s.label}>
                      {Icon && <Icon size={iconSize} aria-hidden />}
                    </a>
                  );
                })}
              </div>
            ) : null}
          </div>

          <div className="footer-nav">
            {useDynamicFooter ? (
              footerGroups.map((group) => (
                <div key={group.id} className="footer-section">
                  {group.title && <h4 className="footer-heading">{group.title}</h4>}
                  <ul className="footer-links">
                    {(group.links || []).map((link) => (
                      <li key={link.id}>
                        <FooterLink link={link} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))
            ) : (
              <>
                <div className="footer-section">
                  <h4 className="footer-heading">{t('shop')}</h4>
                  <ul className="footer-links">
                    <li><Link to="/products">{t('products')}</Link></li>
                    <li><Link to="/products?featured=1">{t('recommendedLink')}</Link></li>
                    <li><Link to="/categories">{t('categories')}</Link></li>
                    <li><Link to="/cart">{t('cart')}</Link></li>
                  </ul>
                </div>
                <div className="footer-section">
                  <h4 className="footer-heading">{t('information')}</h4>
                  <ul className="footer-links">
                    <li><Link to="/about">{t('aboutUs')}</Link></li>
                    <li><Link to="/contact">{t('contact')}</Link></li>
                    <li><Link to="/shipping">{t('shipping')}</Link></li>
                    <li><Link to="/returns">{t('returns')}</Link></li>
                  </ul>
                </div>
                <div className="footer-section">
                  <h4 className="footer-heading">{t('account')}</h4>
                  <ul className="footer-links">
                    <li><Link to="/my-account">{t('myAccount')}</Link></li>
                    <li><Link to="/orders">{t('orders')}</Link></li>
                    <li><Link to="/my-favorites">{t('myFavorites')}</Link></li>
                    <li><Link to="/faq">{t('faq')}</Link></li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="footer-bottom">
          <p className="footer-copyright">
            &copy; {new Date().getFullYear()} {footerBrandName}. {t('allRightsReserved')}
          </p>
          <div className="footer-legal">
            {useDynamicLegal ? (
              legalLinks.map((link) => (
                <FooterLink key={link.id} link={link} />
              ))
            ) : (
              <>
                <Link to="/privacy">{t('privacyPolicy')}</Link>
                <Link to="/terms">{t('termsOfUse')}</Link>
                <Link to="/page/cookies">{t('cookiePolicyTitle')}</Link>
              </>
            )}
            <button type="button" className="footer-legal-btn" onClick={openSettings}>
              {t('cookieSettings')}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
